import { Injectable } from '@angular/core';
import { CappieReporterTypes, FeatureFlag, FeatureFlagsFacade, Logger } from '@mode/shared/contract-common';
import { generateUUID } from '@mode/shared/util-js';
import { Store } from '@ngrx/store';
import { add, compareAsc } from 'date-fns';
import { first } from 'rxjs';
import * as CappieReporterActions from './cappie-reporter.actions';

enum State {
  NotStarted,
  Started,
  Complete,
}

export class CappieReporter {
  private state: State = State.NotStarted;

  private id = '';
  constructor(
    private name: string,
    private rxStore: Store,
    private featureFlagsFacade: FeatureFlagsFacade,
    private logger: Logger
  ) {}

  start(comment: string) {
    if (this.state === State.Started) {
      return this.logger.error('Rendering reporter already started.');
    }
    this.generateId();
    this.changeState(State.Started);
    this.dispatchRenderProgress({
      status: CappieReporterTypes.Status.Start,
      comment,
    });
  }
  progress(comment: string) {
    if (this.state !== State.Started) {
      return this.logger.error('Rendering reporter not started.');
    }
    this.dispatchRenderProgress({
      status: CappieReporterTypes.Status.Progress,
      comment,
    });
  }
  end(comment: string) {
    if (this.state !== State.Started) {
      return this.logger.error('Rendering reporter not started.');
    }
    this.changeState(State.Complete);
    // Cappie service listens for 'end' events to be fired, to know when work is complete, to capture the rendered report.
    // The delay is to allow the charts to paint, after the "ReportRendered" action event.
    // The delay also is to allow work segments to overlap a bit, preventing premature captures bc Cappie sees an 'end' before the next 'start'.
    this.featureFlagsFacade
      .featureValueAsObservable<number>(FeatureFlag.RenderingReporterEventDelay)
      .pipe(first())
      .subscribe((delay) => {
        setTimeout(() => {
          this.dispatchRenderProgress({
            status: CappieReporterTypes.Status.End,
            comment,
          });
        }, delay);
      });
  }

  private dispatchRenderProgress(progressDetails: { status: CappieReporterTypes.Status; comment: string }) {
    const report = { name: this.name, id: this.id, ...progressDetails };
    this.logger.debug('Rendering Event', report);
    this.rxStore.dispatch(CappieReporterActions.progressReporting({ eventData: report }));
  }

  private changeState(state: State) {
    this.state = state;
  }

  private generateId() {
    this.id = generateUUID().slice(0, 7).toLowerCase();
  }
}
@Injectable({
  providedIn: 'root',
})
export class CappieReporterService {
  constructor(
    private $window: Window,
    private rxStore: Store,
    private featureFlagsFacade: FeatureFlagsFacade,
    private logger: Logger
  ) {
    this.registerProgressEventAPI();
  }

  progressEventAPI(name = ''): CappieReporterTypes.ProgressEvents {
    return new CappieReporter(name, this.rxStore, this.featureFlagsFacade, this.logger);
  }

  delayCapture(time: number) {
    const delayTimeTarget = add(Date.now(), { seconds: time });
    const delayReporter = new CappieReporter('cappie-delay', this.rxStore, this.featureFlagsFacade, this.logger);
    delayReporter.start('start cappie delay');

    const pulseIntervalID = setInterval(() => {
      delayReporter.progress('pulse for cappie delay');
      this.checkToEndEvent(delayReporter, delayTimeTarget, pulseIntervalID);
    }, 10000);
  }

  private checkToEndEvent(reporter: CappieReporter, delayTimeTarget: Date, intervalID: NodeJS.Timeout | null) {
    if (compareAsc(Date.now(), delayTimeTarget) === 1) {
      reporter.end('end cappie delay');
      intervalID && clearInterval(intervalID);
    }
  }

  private registerProgressEventAPI() {
    (this.$window as any)['modeCreateRenderingReporter'] = this.progressEventAPI.bind(this);
  }
}
